/* eslint no-useless-escape: off */
import {
    pick,
    isUndefined,
    isString,
    isObject,
    isNumber,
    isEmpty,
    isNull,
    isArray
} from "underscore";
import twttr from "twitter-text";

export const constants = {
    SPECIAL_CHARS_REGEX: /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g,
    SYMBOLS_REGEX: /[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
    NUMBERS_REGEX: /\d/
};

export const rtmpRegex =
    /^((rtmp(s?)):\/\/)(([A-Z0-9][A-Z0-9_-]*)(\.[A-Z0-9][A-Z0-9_-]*)+)(:(\d+))?(\/([A-Z0-9][A-Z0-9_-]*))+(\/)?(\/?\?[A-Z0-9$\-_.+!*'()=&]*)?$/i;

/**
 * @summary Returns an object with a list of satisfied password strength conditions.
 * @function
 *
 * @param password - Password string
 * @returns - { "Password test name": bool }
 */
export const getPasswordStrengthTests = function (
    password: string
): Record<string, boolean> {
    if (!isString(password)) {
        throw new TypeError(
            `getPasswordStrengthTests() - 'password' is not string`
        );
    }
    const invalidChars = [`%`, `\\`];
    return {
        "Min. of 6 characters": password.length >= 6,
        "Max. of 20 characters": password.length <= 20,
        [`Cannot contain (${invalidChars.join(`,`)})`]:
            stringContainsNoInvalidChars(password, invalidChars),
        "Uppercase letter": stringContainsUpperCase(password),
        "Lowercase letter": stringContainsLowerCase(password),
        Number: stringContainsNumbers(password)
    };
};

const stringContainsUpperCase = (string: string) =>
    string.toLowerCase() !== string;

const stringContainsLowerCase = (string: string) =>
    string.toUpperCase() !== string;

const stringContainsNumbers = (string: string) =>
    constants.NUMBERS_REGEX.test(string);

const stringContainsNoInvalidChars = (
    string: string,
    invalidChars: string[]
) => {
    return !invalidChars.some((char) => string.includes(char));
};

/**
 * @summary Parses a URL and returns its most common attribute
 * @function
 *
 * @param url - A String URL
 * @returns object containing the most common URL properties
 */
export const parseUrl = (url: string) => {
    if (!isString(url)) {
        throw new Error(`parseUrl() only accepts strings as arguments`);
    }
    if (isEmpty(url)) {
        throw new Error(`parseUrl() argument cannot be empty`);
    }
    if (url.indexOf(`://`) < 0) {
        throw new Error(`parseUrl() needs a scheme and two slashes`);
    }
    let link = document.createElement(`a`);
    link.href = url;
    const obj = pick(link, [
        `href`,
        `hostname`,
        `port`,
        `search`,
        `pathname`,
        `protocol`
    ]);
    link = null;
    return obj;
};

/**
 * @summary Capitalize a string
 * @function
 *
 * @param str
 * @returns A capitalized string
 */
export const capitalize = (str = ``): string => {
    if (isString(str)) {
        return str.charAt(0).toUpperCase() + str.slice(1);
    } else {
        return ``;
    }
};

/**
 * @summary Splits a compound ID based on the grabyo compound delimiter
 * @function
 *
 * @param compoundId
 * @returns An array of strings
 */
export const splitCompoundId = (compoundId: string): string[] => {
    if (isString(compoundId)) {
        return compoundId.split(App.Constants.COMPOUND_ID_DELIMITER);
    }
};

/**
 * @summary return the last id from a compound ID
 * @function
 *
 * @param compoundId
 * @returns The last ID of the compound
 */
export const getIdFromCompoundId = (compoundId: string): string => {
    if (isString(compoundId)) {
        const [, id] = splitCompoundId(compoundId);
        return id;
    }
};

/**
 * @summary check if a string starts with the given pattern
 * @function
 *
 * @param str
 * @param pattern - A regular expression
 */
export const startsWith = function (
    str = ``,
    pattern: string | RegExp
): boolean {
    if (isString(str) && str.length > 0) {
        if (isString(pattern)) {
            pattern = escapeRegExp(pattern);
        }
        const match = str.match(pattern);
        return isNull(match) ? false : match.index === 0;
    } else {
        return false;
    }
};

/**
 * @summary serializes an object into a query parameter string
 * @function
 *
 * @param params
 * @returns - query parameter
 */
export const serializeQueryParameters = (
    params: ConstructorParameters<typeof URLSearchParams>[0]
): string => {
    if (!isObject(params)) {
        return ``;
    }

    const searchParams = new URLSearchParams(params);
    return searchParams.toString();
};

/**
 * @summary deserializes a query parameter string into an object
 * @function
 *
 * @param searchParams - query parameter
 * @returns params
 */
export const deserializeQueryParameters = (
    searchParams = ``
): Record<string, string> => {
    if (isString(searchParams) && searchParams.length > 0) {
        if (startsWith(searchParams, `?`)) {
            searchParams = searchParams.substr(1);
        }

        const arrayPattern = `[]`;
        const params: Record<string, string> = searchParams
            .split(`&`)
            .reduce((memo, keyVal) => {
                const value = decodeURIComponent(
                    keyVal.substr(keyVal.indexOf(`=`) + 1, keyVal.length)
                );
                let key;
                if (keyVal.indexOf(`=`) > -1) {
                    key = decodeURIComponent(
                        keyVal.substr(0, keyVal.indexOf(`=`))
                    );
                } else {
                    key = value;
                }

                const lastIndexOfArray = key.lastIndexOf(arrayPattern);
                if (
                    lastIndexOfArray > -1 &&
                    key.length - lastIndexOfArray === arrayPattern.length
                ) {
                    const memoKey = key.substr(0, lastIndexOfArray);
                    const paramArray = isArray(memo[memoKey])
                        ? memo[memoKey]
                        : [];
                    paramArray.push(value);
                    memo[memoKey] = paramArray;
                } else {
                    memo[key] = value;
                }

                return memo;
            }, {});

        /**
         * For legacy purpose we need to make sure that `galleryId`
         * is changed to campaignId
         */
        if (!isUndefined(params.campaignId)) {
            params.galleryId = params.campaignId;
            delete params.campaignId;
        }

        return params;
    } else {
        return {};
    }
};

/**
 * @summary escape unsafe characters in a regular expression
 * Referring to the table here:
 * https://developer.mozilla.org/en/JavaScript/Reference/Global_Objects/regexp
 * these characters should be escaped
 * \ ^ $ * + ? . ( ) | { } [ ]
 *
 * @function
 *
 * @param str
 */
export const escapeRegExp = (str = ``): string => {
    if (isString(str)) {
        return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, `\\$&`);
    } else {
        return ``;
    }
};

/**
 * @summary turns a string uppercase and replaces whitespaces with underscores
 *
 * @function
 *
 * @param str
 */
export const valueUppercase = function (str: string): {
    name: string;
    value: string;
} {
    if (isObject(str)) {
        return str; // returns already transformed object if it was passed again
    }
    return {
        name: str,
        value: str.toUpperCase().replace(/ /gi, `_`)
    };
};

/**
 * @summary escape unsafe characters in a regular expression
 * Referring to the table here:
 * https://developer.mozilla.org/en/JavaScript/Reference/Global_Objects/regexp
 * these characters should be escaped
 * \ ^ $ * + ? . ( ) | { } [ ]
 *
 * @function
 *
 * @param str
 */
export const escapeSpecialChars = function (str: string) {
    return str.replace(constants.SPECIAL_CHARS_REGEX, `\\$&`);
};

/**
 * @summary counts how long a string is following Twitter's rules
 *
 * @function
 *
 * @param str
 */
export const countTwitterLength = function (str = ``): number {
    if (isString(str)) {
        const tweetUrls = twttr.extractUrlsWithIndices(str);

        const tweetUrlsLength =
            tweetUrls.length * App.Constants.TWITTER_LINK_LENGTH;

        const tweetWithoutUrls = str
            .split(``)
            .filter((char, index) => {
                for (let i = 0; i < tweetUrls.length; i++) {
                    const {
                        indices: [min, max]
                    } = tweetUrls[i];
                    if (index >= min && index < max) {
                        return false;
                    }
                }
                return true;
            })
            .join(``);

        const tweetTextLength = tweetWithoutUrls
            .split(``)
            .reduce((length, currentChar, index, arr) => {
                const isLastChar = index + 1 > arr.length - 1;
                if (isLastChar) {
                    // This is not a surrogate pair, we treat the char as a normal char
                    return length + calculateCharWeightedLength(currentChar);
                } else {
                    if (isSurrogatePair(currentChar + arr[index + 1])) {
                        // In case of a surrogate pair we are only interested to check
                        // the range of the low code point
                        // We therefore can skip the current char
                        return length;
                    } else {
                        // This is not a surrogate pair, we treat the char as a normal char
                        return (
                            length + calculateCharWeightedLength(currentChar)
                        );
                    }
                }
            }, 0);
        return tweetTextLength + tweetUrlsLength;
    } else {
        throw new TypeError(`parameter \`str\` needs to be a string`);
    }
};

/**
 * @summary check how much meaning a character has and define a weighted length for it.
 * A letter of the latin alphabet contains less information than a chinese character for example
 * It therefore has a lower weighted length.
 * This is based on unicode ranges
 *
 * @function
 *
 * @param str
 */
export const calculateCharWeightedLength = function (str: string): number {
    const charCode = str.charCodeAt(0);
    for (let i = 0; i < App.Constants.TWITTER_CHAR_RANGES.length; i++) {
        const range = App.Constants.TWITTER_CHAR_RANGES[i];
        if (charCode >= range.start && charCode <= range.end) {
            return (str.length * range.weight) / 100;
        }
    }
    return (str.length * App.Constants.TWITTER_DEFAULT_WEIGHTED_LENGTH) / 100;
};

/**
 * @summary Some unicode characters are encoded using UTF-16 which means a pair of UTF-8 character
 * This check if a pair of character falls under that category
 *
 * @function
 *
 * @param str
 */
export const isSurrogatePair = function (str: string): boolean {
    if (isString(str) && str.length === 2) {
        const highCodePoint = str.charCodeAt(0);
        const lowCodePoint = str.charCodeAt(1);
        if (
            highCodePoint >= App.Constants.HIGH_SURROGATE_LOW_LIMIT &&
            highCodePoint <= App.Constants.HIGH_SURROGATE_HIGH_LIMIT
        ) {
            return (
                lowCodePoint >= App.Constants.LOW_SURROGATE_LOW_LIMIT &&
                lowCodePoint <= App.Constants.LOW_SURROGATE_HIGH_LIMIT
            );
        } else {
            return false;
        }
    } else {
        throw new TypeError(
            `A surrogate pair needs to be a string of two characters`
        );
    }
};

/**
 * @summary - Merge a map of query parameters in to a URL string
 *
 * @param url
 * @param newParams
 *
 */
export const mergeQueryParams = (
    url: string,
    newParams: Record<string, string | number>
): string => {
    const urlInstance = new URL(url);

    for (const key of Object.keys(newParams)) {
        urlInstance.searchParams.set(key, newParams[key] as string);
    }

    return urlInstance.href;
};

/**
 * @summary Add a random query parameter to a URL
 * If we do not append a random string to the URL we try to load
 * it will cause issues with CORS settings when cache is enabled
 * Developers, when you test, you won't encounter the bug as you
 * have devtools open that constantly bust the cache.
 *
 * @function
 *
 * @param url
 * @param [randomValue] - The random value to use
 *
 */
export const generateRandomUrl = (
    url: string,
    randomValue: string | number
): string =>
    mergeQueryParams(url, {
        _: randomValue || Math.floor(Math.random() * 99999)
    });

/**
 * @summary Get a file extension given a filename.
 * @param fileName
 */
export const getFileExtension = (fileName: string): string | void => {
    if (!isString(fileName)) {
        throw new TypeError(
            `getFileExtension requires a valid file name string`
        );
    }
    if (fileName.includes(`.`)) {
        return fileName.split(`.`).pop();
    }
};

/**
 * @summary Determines whether a string ends with the characters of a specified string
 * @param string String to be checked
 * @param search String to check for
 * @param length Optional. If provided it is used as the length of `string`. If omitted, the default value is the length of the string
 */
export const endsWith = (string: string, search: string, length?: number) => {
    if (!isString(string) || !isString(search)) {
        throw new TypeError(`Invalid string parameters for endsWith()`);
    }
    if (isEmpty(string) || isEmpty(search)) {
        throw new TypeError(`String parameters for endsWith() cannot be empty`);
    }
    if (!isUndefined(length) && !isNumber(length)) {
        throw new TypeError(`Invalid length parameter for endsWith()`);
    }
    if (isUndefined(length) || length > string.length) {
        length = string.length;
    }
    return string.substring(length - search.length, length) === search;
};

/**
 * @summary Gets the resolution of a stream or campaign as an integer from its encoding profile id
 * @param encodingProfileId the encoding profile id of a campaign or stream
 * @returns stream resolution
 */
export const getResolutionFromEncodingProfileId = (
    encodingProfileId: string
): number => {
    const resolutionString = encodingProfileId
        .split(`-`)
        .filter((str) => str.substr(str.length - 1) === `p`)
        .pop();
    const resolutionInt = resolutionString
        ? parseInt(resolutionString.substr(0, resolutionString.length - 1))
        : 0;
    return resolutionInt;
};

export const checkIfString = (string) =>
    string !== null && typeof string.valueOf() === `string`;

export const validateUrl = (url: string): boolean => {
    try {
        const fullUrl = `https://${url}`;
        new URL(fullUrl);
        return true;
    } catch {
        return false;
    }
};
